import React from 'react';
import {makeStyles} from "@mui/styles";

// MUI Imports
import {
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

// My Imports
import HappyBox from "../layout/HappyBox";

import {colorStyles} from "../../constants/colorStyles";

const useStyles = makeStyles({
    root: {
        padding: '20px', marginTop: '20px', marginBottom: '50px'
    }, title: {
        marginBottom: '20px'
    }, basicsContainer: {
        marginBottom: '20px', '& p': {
            whiteSpace: 'pre-wrap', marginBottom: '20px'
        }
    }, ...colorStyles
});

const IntroversionExtroversionResults = (props) => {
    const classes = useStyles();
    const {results} = props;
    let existTie = false;
    let previousScore = null;

    const STAGES = {
        INTROVERTED: 'Introverted',
        EXTRAVERTED: 'Extraverted'
    };

    return (<>
        <HappyBox>
            <Typography variant='h4' gutterBottom>
                Introversion / Extroversion Test
            </Typography>

            <Typography>
                <b>What is it?</b>{" "}
            </Typography>
            <Typography>
                This test explores personality fit in the workplace. While many aspects of personality influence
                career satisfaction, the introversion–extroversion spectrum plays a particularly significant
                role in determining the types of environments and roles where people thrive.
            </Typography>
            <br></br>
            <Typography>
                <b>Why is it used?</b>{" "}
            </Typography>
            <Typography>
                It’s used because some jobs, while they may align with your interests or motivations, or other
                variables, are more comfortable for certain personality types. For example, introverts often
                thrive in independent, focused roles (like computer-based work), while extroverts may find
                energy and success in social or client-facing roles (like sales). Even if you’re interested in a
                job, if it doesn’t fit your natural personality style, it can quickly become draining.
            </Typography>
        </HappyBox>

        <HappyBox>
            <Typography variant='h4' gutterBottom>
                Your Results
            </Typography>
            <div>
                <Typography variant='h5'>
                    Type: <b>{results.stage}</b>
                </Typography>
            </div>
            <br/>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Score</TableCell>
                            <TableCell>Score</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.scores.map(score => {
                            if (!existTie && previousScore === score.score) existTie = true;
                            previousScore = score.score;
                            return (<TableRow key={score.stage}>
                                <TableCell>
                                    <Chip label={score.stage}
                                          className={classes[score.stage.toLowerCase()]}/>
                                </TableCell>
                                <TableCell>
                                    <LinearProgress variant="determinate" value={(score.score * 100 / 85)}
                                                    className={classes.bar}/>
                                </TableCell>
                                <TableCell>{score.score}</TableCell>
                            </TableRow>)
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <br/>
            {existTie && <Typography>
                In the case of a tie between two scores, we interpret the latter score as the direction you are
                moving toward.
                <br/>
                In this case, your latter score is: <b>{results.stage}</b>
                <br/><br/>
            </Typography>}
            {results.stage === STAGES.INTROVERTED && <div>
                <Typography>
                    <b>Introversion at Work</b><br/><br/>
                </Typography>
                <Typography>
                    You have a mostly introverted personality and work style. Introverts tend to thrive in calm,
                    structured environments where they can think deeply, work independently, and focus without constant
                    interruptions. You may prefer one-on-one or small-group interactions over large meetings and feel
                    most energized when you have time alone to recharge after social or high-stimulation settings.
                </Typography>
                <br/>
                <Typography>
                    In the workplace, introverts often excel in roles that require focus, precision, and thoughtful
                    analysis, such as research, writing, data management, design, or technical problem-solving. You may
                    bring strong listening skills, careful decision-making, and deep concentration to your work.
                </Typography>
                <br/>
                <Typography>
                    Environments that demand frequent social interaction, such as constant meetings, networking events,
                    or high-volume customer-facing roles, can feel draining over time. It’s important to structure your
                    workday to include quiet time for reflection and recovery, as this will help maintain your
                    performance and well-being.
                </Typography>
                <br/>
                <Typography>
                    In short, introversion isn’t about being shy: it’s about how you manage energy. You do your best
                    work when given space to think, prepare, and engage meaningfully rather than constantly perform or
                    socialize.
                </Typography>
            </div>}

            {results.stage === STAGES.EXTRAVERTED && <div>
                <Typography>
                    <b>Extroversion at Work</b><br/><br/>
                </Typography>
                <Typography>
                    You have a mostly extroverted personality and work style. Extroverts gain energy from interaction,
                    collaboration, and connection with others. You thrive in dynamic environments where ideas are
                    exchanged freely, and teamwork or communication is central to success.
                </Typography>
                <br/>
                <Typography>
                    In the workplace, extroverts often excel in roles that involve networking, leadership,
                    communication, or public engagement (e.g., sales, management, teaching, marketing, or team-based
                    project work). You likely bring enthusiasm, quick thinking, and a natural ability to motivate and
                    connect with others.
                </Typography>
                <br/>
                <Typography>
                    Extroverts often think best out loud and process information through dialogue rather than quiet
                    reflection. You’re likely to approach new situations with openness and confidence, quickly building
                    rapport with colleagues and clients alike.
                </Typography>
                <br/>
                <Typography>
                    Environments that are quiet or isolated (e.g. working slone from home) will feel draining or
                    uninspiring over time. To stay energized, it’s helpful to seek out opportunities for social
                    engagement such as brainstorming sessions, group discussions, or mentorship throughout the day.
                </Typography>
                <br/>
                <Typography>
                    In short, extroversion isn’t about being loud or attention-seeking—it’s about getting your energy
                    from being around and collaborating with others. You do your best work when surrounded by people,
                    energy, and exchange.
                </Typography>
            </div>}
        </HappyBox>

    </>)
}

export default IntroversionExtroversionResults
